import React, { useEffect, useState } from "react";
import Card from "@ui-kit/data-display/Card/Card.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { enums, PAYMENT_TYPE_OPTIONS_MAP } from "@shared/constants";
import { NestedPublicVenueSerializer } from "@app-contexts/public/features/event-detail/data/types/events";
import { PublicMembershipGroupSerializer } from "@shared/modules/memberships/types";
import usePublicProductsService from "@app-contexts/public/features/products/data/services/usePublicProductsService";
import { ProductTypeSerializer } from "@app-contexts/public/features/products/data/types/products";
import Box from "@ui-kit/layout/Box/Box.web";
import usePublicMembershipGroupsService from "@shared/modules/memberships/data/services/usePublicMembershipGroupsService";
import { StandardAttractionItem } from "@app-contexts/dashboard/features/events/data/types/events";
import useDjangoWaffle from "@shared/hooks/djangoWaffle/useDjangoWaffle";
import ProductAddons from "@app-contexts/public/shared/checkout/components/steps/add-ons/product-add-ons/ProductAddons.web";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import { getEmbeddedPurchasePlatform } from "@shared/modules/financials/utils/purchase-platform-utils";
import { PAGE_SIZE } from "@app-contexts/public/shared/checkout/components/steps/add-ons/product-add-ons/ProductAddonGrid";
import ParsedHtml from "@ui-kit/typography/ParsedHtml/ParsedHtml.web";
import { AttractionSliderWithHeader } from "./AttractionSliderWithHeader.web";

// Use StandardAttractionItem since CardSlider only handles non-redirect types
type AttractionItemType = StandardAttractionItem;

export interface AttractionCardSliderProps {
  attractionItem: AttractionItemType;
  venue: NestedPublicVenueSerializer;
  isBasketUpdating: boolean;
}

export const AttractionCardSlider: React.FC<AttractionCardSliderProps> = ({
  attractionItem,
  venue,
  isBasketUpdating,
}) => {
  // itemData can either be a product array or membership group array
  const [itemData, setItemData] = useState<
    PublicMembershipGroupSerializer[] | ProductTypeSerializer[]
  >([]);
  const publicBasket = usePublicBasket({
    purchase_source_platform: getEmbeddedPurchasePlatform(),
  });

  // params for API calls
  const params = {
    purchase_platform: enums.PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_WEB.id,
    payment_type: PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id,
    id__in: attractionItem.ids.toString(),
    sort: "category__sort_order,category__name,sort_order,name",
    page_size: PAGE_SIZE,
  };

  const { usePaginated: usePaginatedProducts } = usePublicProductsService();
  const {
    data: productsData,
    isLoading: isLoadingProducts,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = usePaginatedProducts({
    params,
    options: {
      enabled:
        attractionItem.type === enums.ATTRACTION_CHILD_TYPE_MAP.PRODUCT.id,
    },
  });

  const { usePaginated: usePaginatedMemberships } =
    usePublicMembershipGroupsService();
  const { data: membershipsData, isLoading: isLoadingMemberships } =
    usePaginatedMemberships({
      params,
      options: {
        enabled:
          attractionItem.type === enums.ATTRACTION_CHILD_TYPE_MAP.MEMBERSHIP.id,
      },
    });

  const { isFlagActive } = useDjangoWaffle();
  const type =
    attractionItem.type === enums.ATTRACTION_CHILD_TYPE_MAP.PRODUCT.id
      ? "product"
      : "membership";
  const renderAsGrid =
    isFlagActive(`${type}_addons_render_as_grid`, venue.id) &&
    itemData.length > 0 &&
    attractionItem.type === enums.ATTRACTION_CHILD_TYPE_MAP.PRODUCT.id;

  const showAllByDefault = isFlagActive(
    `${type}_addons_show_all_by_default`,
    venue.id
  );

  useEffect(() => {
    // Load data for products
    if (
      productsData &&
      attractionItem.type === enums.ATTRACTION_CHILD_TYPE_MAP.PRODUCT.id
    ) {
      const flattenedProductsArray =
        productsData && productsData.pages && productsData.pages.length
          ? productsData.pages?.map((page) => page?.results).flat()
          : ([] as ProductTypeSerializer[]);
      setItemData(flattenedProductsArray);
    }
    // Load data for memberships
    else if (
      membershipsData &&
      attractionItem.type === enums.ATTRACTION_CHILD_TYPE_MAP.MEMBERSHIP.id
    ) {
      const flattenedMembershipsArray =
        membershipsData && membershipsData.pages && membershipsData.pages.length
          ? membershipsData.pages?.map((page) => page?.results).flat()
          : ([] as PublicMembershipGroupSerializer[]);
      setItemData(flattenedMembershipsArray);
    }
  }, [productsData, membershipsData, attractionItem.type]);

  return (
    <Box>
      <Card flexDirection="column" w="full" rounded={{ base: 0, sm: "lg" }}>
        {renderAsGrid ? (
          // compensate for the marginTop: -34px inside of Swiper
          <Box paddingTop={renderAsGrid ? "0px" : "34px"}>
            <Text
              fontSize={{ base: "md", sm: "lg" }}
              fontWeight="bold"
              color="primary.600"
              marginBottom="md"
            >
              {attractionItem.title}
            </Text>

            <Text pb="lg" fontSize="md" color="fg.muted">
              <ParsedHtml htmlString={attractionItem.description} />
            </Text>

            <ProductAddons.Products
              products={itemData as ProductTypeSerializer[]}
              publicBasket={publicBasket}
              modalHeight="65vh"
              renderAsGrid={renderAsGrid}
              showAll={showAllByDefault}
              onLoadMore={fetchNextPage}
              isLoading={isLoadingProducts || isFetchingNextPage}
              hasMore={hasNextPage}
              venue={venue}
            />
          </Box>
        ) : (
          <AttractionSliderWithHeader
            attractionItem={attractionItem}
            venue={venue}
            itemData={itemData}
            isLoading={isLoadingProducts || isLoadingMemberships}
            isBasketUpdating={isBasketUpdating}
            renderAsGrid={renderAsGrid}
          />
        )}
      </Card>
    </Box>
  );
};

export default AttractionCardSlider;
